<script setup lang="ts">
import { ref, computed } from 'vue'
import { NInput, NSwitch, NButton } from 'naive-ui'
import VehicleCard from './VehicleCard.vue'
import EmptyStateIllustration from '@/components/commons/EmptyStateIllustration.vue'
import type { Vehicle } from '@/api/vehicles'

const props = defineProps<{ vehicles: Vehicle[] }>()
const emit = defineEmits<{
  add: []
  edit: [v: Vehicle]
  delete: [v: Vehicle]
}>()

const searchQuery = ref('')
const showRetired = ref(false)

const filtered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  return props.vehicles.filter((v) => {
    if (!showRetired.value && !v.is_active) return false
    if (q && !v.name.toLowerCase().includes(q)) return false
    return true
  })
})
</script>

<template>
  <div class="fleet-cards">
    <div class="toolbar">
      <n-input
        v-model:value="searchQuery"
        placeholder="Поиск по названию"
        clearable
        size="small"
        style="max-width: 280px"
      />
      <label class="retired-toggle">
        <n-switch v-model:value="showRetired" />
        <span>Показать выведенные</span>
      </label>
      <div class="right-group">
        <slot name="view-toggle" />
        <n-button type="primary" data-action="add" @click="emit('add')">
          + Добавить машину
        </n-button>
      </div>
    </div>

    <div v-if="filtered.length === 0" class="empty">
      <EmptyStateIllustration
        type="no-results"
        title="Машин не найдено"
        :description="searchQuery ? 'Попробуйте другой запрос или включите «Показать выведенные».' : 'Создайте первую машину.'"
        :action-label="searchQuery ? undefined : '+ Добавить машину'"
        @action="emit('add')"
      />
    </div>

    <div v-else class="grid">
      <VehicleCard
        v-for="v in filtered"
        :key="v.id"
        :vehicle="v"
        @edit="(x) => emit('edit', x)"
        @delete="(x) => emit('delete', x)"
      />
    </div>
  </div>
</template>

<style scoped>
.fleet-cards { display: flex; flex-direction: column; gap: var(--sp-3); }
.toolbar {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
  padding: var(--sp-3) var(--sp-4);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  box-shadow: var(--shadow-1);
  margin-bottom: var(--sp-4);
}
.retired-toggle { display: inline-flex; align-items: center; gap: 8px; font-size: var(--fs-sm); }
.right-group {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-3);
  margin-left: auto;
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--sp-4);
}
.empty { padding: var(--sp-5) 0; }
@media (max-width: 767px) {
  .toolbar { flex-direction: column; align-items: stretch; }
  .right-group { margin-left: 0; flex-wrap: wrap; }
}
</style>
